import { useEffect } from 'react';
import { ACTION_TYPES, useBannerDispatch, useBannerState } from './banner-context';
import BannerAppService from '../../../../application/platform/apm/banner/banner-app-service';
import { IBannerResponse } from '../../../../model/apm/banner/response/banner-response';
import { IBannerAddEditRequest } from '../../../../model/apm/banner/request/banner-add-edit-request';
import { reorderOptimistic } from '../../components/reorder-utils';
import { useActionRunner } from '../../../../hooks/use-action-runner';
import { removeById, upsertById } from '../../../../utils/list-utils';

export const useBannerController = () => {
    const dispatch = useBannerDispatch();
    const state = useBannerState();
    const { isSaving, runSaving, run, showSuccess, confirmDelete } = useActionRunner();

    async function loadBanners() {
        dispatch({ type: ACTION_TYPES.setLoading, payload: true });
        await run('Não foi possível carregar os banners', async () => {
            dispatch({ type: ACTION_TYPES.setBanners, payload: await BannerAppService.list() });
        });
        dispatch({ type: ACTION_TYPES.setLoading, payload: false });
    }

    useEffect(() => {
        loadBanners();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function setBanners(banners: IBannerResponse[]) {
        dispatch({ type: ACTION_TYPES.setBanners, payload: banners });
    }

    function replaceBanner(saved: IBannerResponse) {
        setBanners(upsertById(state.banners, saved));
    }

    function openDrawer(banner?: IBannerResponse) {
        dispatch({ type: ACTION_TYPES.openDrawer, payload: banner });
    }

    function closeDrawer() {
        dispatch({ type: ACTION_TYPES.closeDrawer });
    }

    function handleSave(request: IBannerAddEditRequest) {
        const editing = state.drawer.banner;
        return runSaving('Não foi possível salvar o banner', async () => {
            replaceBanner(await (editing ? BannerAppService.edit(editing.id, request) : BannerAppService.add(request)));
            showSuccess(editing ? 'Banner atualizado com sucesso!' : 'Banner cadastrado como rascunho. Publique-o para exibir no portal.');
            closeDrawer();
        });
    }

    function handleTogglePublication(banner: IBannerResponse) {
        const isPublished = !banner.isPublished;
        run('Não foi possível alterar a publicação do banner', async () => {
            replaceBanner(await BannerAppService.changePublication(banner.id, isPublished));
            showSuccess(isPublished ? 'Banner publicado com sucesso!' : 'Banner despublicado. Ele não aparece mais no portal.');
        });
    }

    function handleDelete(banner: IBannerResponse) {
        confirmDelete(`Tem certeza que deseja excluir o banner "${banner.title}"?`, () => run('Não foi possível excluir o banner', async () => {
            await BannerAppService.remove(banner.id);
            setBanners(removeById(state.banners, banner.id));
            showSuccess('Banner excluído com sucesso!');
        }));
    }

    function handleReorder(next: IBannerResponse[]) {
        run('Não foi possível reordenar os banners', () => reorderOptimistic(state.banners, next, (b) => b.id, setBanners, BannerAppService.reorder));
    }

    return { isSaving, openDrawer, closeDrawer, handleSave, handleTogglePublication, handleDelete, handleReorder };
};
